/**
 * Push notification lifecycle for the Moon app.
 *
 * Permission inspection is safe on mount. The OS permission prompt is only
 * reached through the returned requestPermissions callback from a notification
 * preference interaction.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, Linking, Platform } from "react-native";
import * as Device from "expo-device";
import Constants from "expo-constants";
import * as Notifications from "expo-notifications";
import type { NotificationPermissionsStatus } from "expo-notifications";
import {
  isNotificationPermissionAvailable,
  type NotificationPermissionState,
  type PushTokenResolutionState,
} from "./notification-deliverability";

if (Platform.OS !== "web") {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });
}

export interface PushNotification {
  request: {
    identifier?: string;
    content: {
      title?: string | null;
      body?: string | null;
      data?: Record<string, unknown>;
    };
  };
}

export interface UsePushNotificationsOptions {
  onNotificationReceived?: (notification: PushNotification) => void;
  onNotificationResponse?: (notification: PushNotification) => void;
}

export interface UsePushNotificationsResult {
  /** The Expo push token (null if not available). Never render or log this. */
  pushToken: string | null;
  /** Whether permission/token inspection is in progress. */
  isLoading: boolean;
  /** Any permission or token-resolution error. */
  error: string | null;
  /** The last notification received while app was foregrounded. */
  notification: PushNotification | null;
  /** Current device permission state. */
  permissionState: NotificationPermissionState;
  /** Current token resolution state. */
  tokenResolutionState: PushTokenResolutionState;
  /** Inspect permission and resolve a token when permission already exists. */
  refreshPermission: () => Promise<void>;
  /** Request permission from a contextual preference action, then resolve the token. */
  requestPermissions: () => Promise<string | null>;
  /** Open the native app settings screen for permission recovery. */
  openDeviceSettings: () => Promise<void>;
}

function getPermissionState(
  permissions: NotificationPermissionsStatus,
): NotificationPermissionState {
  if (Platform.OS === "web" || !Device.isDevice) return "unsupported";

  const iosStatus = permissions.ios?.status;
  if (Platform.OS === "ios" && iosStatus !== undefined) {
    if (iosStatus === Notifications.IosAuthorizationStatus.NOT_DETERMINED) {
      return "not_requested";
    }
    if (iosStatus === Notifications.IosAuthorizationStatus.PROVISIONAL) {
      return "provisional";
    }
    if (iosStatus === Notifications.IosAuthorizationStatus.EPHEMERAL) {
      return "limited";
    }
    if (
      iosStatus === Notifications.IosAuthorizationStatus.AUTHORIZED &&
      permissions.ios?.allowsAlert === false
    ) {
      return "restricted";
    }
    if (iosStatus === Notifications.IosAuthorizationStatus.AUTHORIZED) {
      return "authorized";
    }
    if (iosStatus === Notifications.IosAuthorizationStatus.DENIED) {
      return "denied";
    }
  }

  if (permissions.status === "undetermined") return "not_requested";
  if (permissions.status === "granted") return "authorized";
  return "denied";
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {},
): UsePushNotificationsResult {
  const { onNotificationReceived, onNotificationResponse } = options;
  const [pushToken, setPushToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionState, setPermissionState] =
    useState<NotificationPermissionState>(
      Platform.OS === "web" || !Device.isDevice
        ? "unsupported"
        : "not_requested",
    );
  const [tokenResolutionState, setTokenResolutionState] =
    useState<PushTokenResolutionState>("idle");
  const [notification, setNotification] = useState<PushNotification | null>(
    null,
  );

  const notificationListener = useRef<{ remove: () => void } | null>(null);
  const responseListener = useRef<{ remove: () => void } | null>(null);

  const resolvePermissionAndToken = useCallback(
    async (shouldRequestPermission: boolean): Promise<string | null> => {
      if (Platform.OS === "web" || !Device.isDevice) {
        setPermissionState("unsupported");
        setPushToken(null);
        setTokenResolutionState("idle");
        setError(null);
        setIsLoading(false);
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        let permissions = await Notifications.getPermissionsAsync();
        let nextPermissionState = getPermissionState(permissions);

        // A denied/restricted permission is recovered from native Settings;
        // asking again here would either do nothing or create a repeat-prompt
        // loop on platforms that suppress a second system prompt.
        if (
          shouldRequestPermission &&
          nextPermissionState === "not_requested"
        ) {
          permissions = await Notifications.requestPermissionsAsync({
            ios: {
              allowAlert: true,
              allowBadge: true,
              allowSound: true,
            },
          });
          nextPermissionState = getPermissionState(permissions);
        }

        setPermissionState(nextPermissionState);
        if (!isNotificationPermissionAvailable(nextPermissionState)) {
          setPushToken(null);
          setTokenResolutionState("idle");
          setIsLoading(false);
          return null;
        }

        setTokenResolutionState("pending");
        if (Platform.OS === "android") {
          await Notifications.setNotificationChannelAsync("reminders", {
            name: "Reminders",
            importance: Notifications.AndroidImportance.HIGH,
            vibrationPattern: [0, 250, 250, 250],
            lightColor: "#C4B5FD",
          });
        }

        const projectId = Constants.expoConfig?.extra?.eas?.projectId;
        const token = await Notifications.getExpoPushTokenAsync({ projectId });
        setPushToken(token.data);
        setTokenResolutionState("resolved");
        setIsLoading(false);
        return token.data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to get push token";
        setPushToken(null);
        setTokenResolutionState("failed");
        setError(message);
        setIsLoading(false);
        console.error(
          "[usePushNotifications] Token resolution failed:",
          message,
        );
        return null;
      }
    },
    [],
  );

  const refreshPermission = useCallback(async () => {
    await resolvePermissionAndToken(false);
  }, [resolvePermissionAndToken]);

  const requestPermissions = useCallback(async () => {
    return resolvePermissionAndToken(true);
  }, [resolvePermissionAndToken]);

  const openDeviceSettings = useCallback(async () => {
    if (Platform.OS === "web") return;
    await Linking.openSettings();
  }, []);

  useEffect(() => {
    void refreshPermission();

    notificationListener.current =
      Notifications.addNotificationReceivedListener((notif: unknown) => {
        const nextNotification = notif as PushNotification;
        setNotification(nextNotification);
        onNotificationReceived?.(nextNotification);
      });

    responseListener.current =
      Notifications.addNotificationResponseReceivedListener(
        (response: { notification: PushNotification }) => {
          onNotificationResponse?.(response.notification);
        },
      );

    const appStateSubscription = AppState.addEventListener(
      "change",
      (state) => {
        if (state === "active") void refreshPermission();
      },
    );

    return () => {
      notificationListener.current?.remove();
      responseListener.current?.remove();
      appStateSubscription.remove();
    };
  }, [onNotificationReceived, onNotificationResponse, refreshPermission]);

  return {
    pushToken,
    isLoading,
    error,
    notification,
    permissionState,
    tokenResolutionState,
    refreshPermission,
    requestPermissions,
    openDeviceSettings,
  };
}

/**
 * Get a push token without requesting permission. This is intentionally
 * read-only so background/authentication lifecycle code cannot prompt.
 */
export async function getPushToken(): Promise<string | null> {
  if (Platform.OS === "web" || !Device.isDevice) return null;

  try {
    const permissions = await Notifications.getPermissionsAsync();
    if (!isNotificationPermissionAvailable(getPermissionState(permissions))) {
      return null;
    }

    const projectId = Constants.expoConfig?.extra?.eas?.projectId;
    const token = await Notifications.getExpoPushTokenAsync({ projectId });
    return token.data;
  } catch {
    return null;
  }
}
